import { useCallback, useEffect, useState } from "react";
import { Users2, Mail, Loader2, Plus, X, Trash2, Heart, Send, MessageCircle, ArrowLeft } from "lucide-react";
import { toast } from "sonner";
import OSLayout from "../../components/os/OSLayout";
import { api } from "../../lib/api";
import { useAuth } from "../../context/AuthContext";

const NEWS_TONE = { borrador: "bg-gold/15 text-gold", enviado: "bg-emerald-500/15 text-emerald-600" };

function Modal({ title, onClose, children, testid }) {
  return (
    <div className="fixed inset-0 z-50 bg-navy/40 backdrop-blur-sm flex items-center justify-center p-4" data-testid={testid}>
      <div className="bg-white rounded-2xl w-full max-w-md p-6 max-h-[90vh] overflow-y-auto">
        <div className="flex items-center justify-between mb-4"><h3 className="font-editorial text-2xl">{title}</h3><button onClick={onClose} className="text-navy/40 hover:text-navy"><X className="w-5 h-5" /></button></div>
        {children}
      </div>
    </div>
  );
}

export default function InternalComms() {
  const [meta, setMeta] = useState(null);
  const [tab, setTab] = useState("communities");
  useEffect(() => { api.get("/comms/meta").then((r) => setMeta(r.data)).catch(() => setMeta({ is_leader: false, areas: [] })); }, []);
  const tabs = [["communities", "Comunidades", Users2]];
  if (meta?.is_leader) tabs.push(["newsletter", "Newsletter / Broadcast", Mail]);
  return (
    <OSLayout title="OMNIA Comunicación Interna">
      <div className="space-y-6" data-testid="comms-page">
        <div className="flex gap-2 border-b border-navy/10 pb-2">
          {tabs.map(([k, l, Ic]) => (
            <button key={k} onClick={() => setTab(k)} data-testid={`ic-tab-${k}`} className={`inline-flex items-center gap-2 px-4 py-2 rounded-full text-sm ${tab === k ? "bg-navy text-cream" : "text-navy/55 hover:bg-navy/5"}`}><Ic className="w-4 h-4" /> {l}</button>
          ))}
        </div>
        {!meta ? <div className="py-16 flex justify-center text-navy/40"><Loader2 className="w-6 h-6 animate-spin" /></div> : (
          <>
            {tab === "communities" && <Communities />}
            {tab === "newsletter" && meta.is_leader && <Newsletters meta={meta} />}
          </>
        )}
      </div>
    </OSLayout>
  );
}

/* ===================== Communities ===================== */
function Communities() {
  const [items, setItems] = useState([]);
  const [open, setOpen] = useState(false);
  const [active, setActive] = useState(null);
  const load = useCallback(() => { api.get("/comms/communities").then((r) => setItems(r.data || [])).catch(() => {}); }, []);
  useEffect(() => { load(); }, [load]);
  const toggle = async (id) => { try { await api.post(`/comms/communities/${id}/toggle`); load(); } catch { toast.error("Error"); } };
  const del = async (id) => { try { await api.delete(`/comms/communities/${id}`); load(); } catch (e) { toast.error(e?.response?.data?.detail || "Error"); } };
  if (active) return <CommunityFeed community={active} onBack={() => { setActive(null); load(); }} />;
  return (
    <div className="space-y-3">
      <button onClick={() => setOpen(true)} className="btn btn-primary gap-2" data-testid="ic-comm-new"><Plus className="w-4 h-4" /> Nueva comunidad</button>
      {items.length === 0 ? <div className="bg-white border border-navy/10 rounded-2xl p-6 text-sm text-navy/40">Sin comunidades.</div>
        : <div className="grid gap-3 sm:grid-cols-2">{items.map((c) => (
          <div key={c.id} className="bg-white border border-navy/10 rounded-2xl p-4" data-testid={`ic-comm-${c.id}`}>
            <div className="flex items-start justify-between">
              <div><span className="text-[10px] uppercase tracking-wide bg-violet/10 text-violet px-2 py-0.5 rounded-full">{c.topic}</span><div className="text-sm font-medium mt-2">{c.name}</div>{c.description && <div className="text-xs text-navy/50 mt-0.5">{c.description}</div>}</div>
              <button onClick={() => del(c.id)} className="text-navy/30 hover:text-destructive"><Trash2 className="w-3.5 h-3.5" /></button>
            </div>
            <div className="text-xs text-navy/40 mt-2">{c.member_count} miembros · {c.post_count} publicaciones</div>
            <div className="flex gap-2 mt-3">
              <button onClick={() => setActive(c)} className="btn btn-ghost text-xs gap-1" data-testid={`ic-comm-open-${c.id}`}><MessageCircle className="w-3.5 h-3.5" /> Abrir</button>
              <button onClick={() => toggle(c.id)} className={`btn text-xs ${c.joined ? "btn-ghost" : "btn-primary"}`} data-testid={`ic-comm-toggle-${c.id}`}>{c.joined ? "Salir" : "Unirse"}</button>
            </div>
          </div>
        ))}</div>}
      {open && <CommunityDialog onClose={() => setOpen(false)} onSaved={() => { setOpen(false); load(); }} />}
    </div>
  );
}

function CommunityDialog({ onClose, onSaved }) {
  const [f, setF] = useState({ name: "", description: "", topic: "General" });
  const [busy, setBusy] = useState(false);
  const save = async () => {
    if (!f.name.trim()) return toast.error("Nombre requerido");
    setBusy(true);
    try { await api.post("/comms/communities", f); toast.success("Comunidad creada"); onSaved(); }
    catch (e) { toast.error(e?.response?.data?.detail || "Error"); } finally { setBusy(false); }
  };
  return (
    <Modal title="Nueva comunidad" onClose={onClose} testid="ic-comm-dialog">
      <div className="space-y-3">
        <input className="field" placeholder="Nombre" value={f.name} onChange={(e) => setF({ ...f, name: e.target.value })} data-testid="ic-comm-name" />
        <input className="field" placeholder="Tema (General, Ventas, Cultura…)" value={f.topic} onChange={(e) => setF({ ...f, topic: e.target.value })} data-testid="ic-comm-topic" />
        <textarea className="field" rows={2} placeholder="Descripción" value={f.description} onChange={(e) => setF({ ...f, description: e.target.value })} />
      </div>
      <div className="flex justify-end gap-2 mt-5"><button onClick={onClose} className="btn btn-ghost">Cancelar</button><button onClick={save} disabled={busy} className="btn btn-primary" data-testid="ic-comm-save">{busy ? <Loader2 className="w-4 h-4 animate-spin" /> : "Crear"}</button></div>
    </Modal>
  );
}

function CommunityFeed({ community, onBack }) {
  const { user } = useAuth();
  const [posts, setPosts] = useState([]);
  const [text, setText] = useState("");
  const [busy, setBusy] = useState(false);
  const load = useCallback(() => { api.get(`/comms/communities/${community.id}/posts`).then((r) => setPosts(r.data || [])).catch(() => {}); }, [community.id]);
  useEffect(() => { load(); }, [load]);
  const post = async () => {
    if (!text.trim()) return;
    setBusy(true);
    try { await api.post(`/comms/communities/${community.id}/posts`, { content: text }); setText(""); load(); }
    catch (e) { toast.error(e?.response?.data?.detail || "Error"); } finally { setBusy(false); }
  };
  const like = async (id) => { try { await api.post(`/comms/posts/${id}/like`); load(); } catch { toast.error("Error"); } };
  const myId = user?.id;
  return (
    <div className="space-y-4" data-testid="ic-comm-feed">
      <button onClick={onBack} className="btn btn-ghost gap-1 text-sm" data-testid="ic-comm-back"><ArrowLeft className="w-4 h-4" /> Comunidades</button>
      <div><span className="text-[10px] uppercase tracking-wide bg-violet/10 text-violet px-2 py-0.5 rounded-full">{community.topic}</span><h2 className="font-editorial text-2xl mt-1">{community.name}</h2>{community.description && <p className="text-sm text-navy/50">{community.description}</p>}</div>
      <div className="bg-white border border-navy/10 rounded-2xl p-4 flex items-start gap-2">
        <textarea className="field flex-1" rows={2} placeholder="Comparte algo con la comunidad…" value={text} onChange={(e) => setText(e.target.value)} data-testid="ic-post-input" />
        <button onClick={post} disabled={busy} className="btn btn-primary gap-1" data-testid="ic-post-submit">{busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" />}</button>
      </div>
      {posts.length === 0 ? <div className="text-sm text-navy/40 py-4">Sé el primero en publicar.</div>
        : posts.map((p) => (
          <div key={p.id} className="bg-white border border-navy/10 rounded-2xl p-4" data-testid={`ic-post-${p.id}`}>
            <div className="text-xs text-navy/40">{p.user_name} · {new Date(p.at).toLocaleString("es-MX")}</div>
            <div className="text-sm text-navy/80 mt-1 whitespace-pre-wrap">{p.content}</div>
            <button onClick={() => like(p.id)} className={`inline-flex items-center gap-1 text-xs mt-2 ${(p.likes || []).includes(myId) ? "text-destructive" : "text-navy/40 hover:text-destructive"}`} data-testid={`ic-post-like-${p.id}`}>
              <Heart className={`w-3.5 h-3.5 ${(p.likes || []).includes(myId) ? "fill-current" : ""}`} /> {(p.likes || []).length}
            </button>
          </div>
        ))}
    </div>
  );
}

/* ===================== Newsletter / Broadcast ===================== */
function Newsletters({ meta }) {
  const [items, setItems] = useState([]);
  const [open, setOpen] = useState(false);
  const load = useCallback(() => { api.get("/comms/newsletters").then((r) => setItems(r.data || [])).catch(() => {}); }, []);
  useEffect(() => { load(); }, [load]);
  const send = async (id) => { try { const r = await api.post(`/comms/newsletters/${id}/send`); toast.success(`Enviado a ${r.data.sent} personas`); load(); } catch (e) { toast.error(e?.response?.data?.detail || "Error"); } };
  const del = async (id) => { try { await api.delete(`/comms/newsletters/${id}`); load(); } catch { toast.error("Error"); } };
  return (
    <div className="space-y-3">
      <button onClick={() => setOpen(true)} className="btn btn-primary gap-2" data-testid="ic-news-new"><Plus className="w-4 h-4" /> Nuevo boletín</button>
      {items.length === 0 ? <div className="bg-white border border-navy/10 rounded-2xl p-6 text-sm text-navy/40">Sin boletines.</div>
        : items.map((n) => (
          <div key={n.id} className="bg-white border border-navy/10 rounded-2xl p-4" data-testid={`ic-news-${n.id}`}>
            <div className="flex items-start justify-between">
              <div><div className="text-sm font-medium">{n.subject}</div><div className="text-xs text-navy/50 mt-0.5 line-clamp-2">{n.body}</div><div className="text-xs text-navy/40 mt-1">Audiencia: {n.audience === "todos" ? "Todos" : n.audience.replace("area:", "Área · ")}{n.status === "enviado" && ` · ${n.sent_count} enviados`}</div></div>
              <div className="flex items-center gap-2"><span className={`text-xs px-2 py-0.5 rounded-full ${NEWS_TONE[n.status]}`}>{n.status}</span><button onClick={() => del(n.id)} className="text-navy/30 hover:text-destructive"><Trash2 className="w-3.5 h-3.5" /></button></div>
            </div>
            {n.status === "borrador" && <button onClick={() => send(n.id)} className="btn btn-primary text-xs gap-1 mt-2" data-testid={`ic-news-send-${n.id}`}><Send className="w-3.5 h-3.5" /> Enviar ahora</button>}
          </div>
        ))}
      {open && <NewsletterDialog meta={meta} onClose={() => setOpen(false)} onSaved={() => { setOpen(false); load(); }} />}
    </div>
  );
}

function NewsletterDialog({ meta, onClose, onSaved }) {
  const [f, setF] = useState({ subject: "", body: "", audience: "todos" });
  const [busy, setBusy] = useState(false);
  const save = async () => {
    if (!f.subject.trim() || !f.body.trim()) return toast.error("Asunto y contenido requeridos");
    setBusy(true);
    try { await api.post("/comms/newsletters", f); toast.success("Boletín guardado"); onSaved(); }
    catch (e) { toast.error(e?.response?.data?.detail || "Error"); } finally { setBusy(false); }
  };
  return (
    <Modal title="Nuevo boletín" onClose={onClose} testid="ic-news-dialog">
      <div className="space-y-3">
        <input className="field" placeholder="Asunto" value={f.subject} onChange={(e) => setF({ ...f, subject: e.target.value })} data-testid="ic-news-subject" />
        <textarea className="field" rows={6} placeholder="Contenido del boletín…" value={f.body} onChange={(e) => setF({ ...f, body: e.target.value })} data-testid="ic-news-body" />
        <div>
          <label className="text-xs font-medium text-navy/60 mb-1 block">Audiencia</label>
          <select className="field" value={f.audience} onChange={(e) => setF({ ...f, audience: e.target.value })} data-testid="ic-news-audience">
            <option value="todos">Todos los colaboradores</option>
            {(meta.areas || []).map((a) => <option key={a} value={`area:${a}`}>Área · {a}</option>)}
          </select>
        </div>
        <p className="text-xs text-navy/40">Se enviará como notificación in-app y correo (Resend) a la audiencia seleccionada.</p>
      </div>
      <div className="flex justify-end gap-2 mt-5"><button onClick={onClose} className="btn btn-ghost">Cancelar</button><button onClick={save} disabled={busy} className="btn btn-primary" data-testid="ic-news-save">{busy ? <Loader2 className="w-4 h-4 animate-spin" /> : "Guardar"}</button></div>
    </Modal>
  );
}
